import _ from 'underscore';
import './number-is-nan';
import * as cron from './cron-parser-wrapper';

const FREQUENCY_KEYS_UPPER = {
  [cron.frequency.NONE]: 'CronFormatter_FrequencyGrain_DropdownMenu_None',
  [cron.frequency.MONTHLY]: 'CronFormatter_FrequencyGrain_DropdownMenu_Monthly',
  [cron.frequency.WEEKLY]: 'CronFormatter_FrequencyGrain_DropdownMenu_Weekly',
  [cron.frequency.DAILY]: 'CronFormatter_FrequencyGrain_DropdownMenu_Daily',
  [cron.frequency.ONCE]: 'CronFormatter_FrequencyGrain_DropdownMenu_Once',
  [cron.frequency.HOURLY]: 'CronFormatter_FrequencyGrain_DropdownMenu_Hourly',
  [cron.frequency.EVERY_6_HOURS]: 'CronFormatter_FrequencyGrain_DropdownMenu_Every_6_Hours',
  [cron.frequency.EVERY_12_HOURS]: 'CronFormatter_FrequencyGrain_DropdownMenu_Every_12_Hours',
};
const FREQUENCY_KEYS_LOWER = {
  [cron.frequency.NONE]: 'CronFormatter_FrequencyGrain_Runs_None',
  [cron.frequency.MONTHLY]: 'CronFormatter_FrequencyGrain_Runs_Monthly',
  [cron.frequency.WEEKLY]: 'CronFormatter_FrequencyGrain_Runs_Weekly',
  [cron.frequency.DAILY]: 'CronFormatter_FrequencyGrain_Runs_Daily',
  [cron.frequency.ONCE]: 'CronFormatter_FrequencyGrain_Runs_Once',
  [cron.frequency.HOURLY]: 'CronFormatter_FrequencyGrain_Runs_Hourly',
  [cron.frequency.EVERY_6_HOURS]: 'CronFormatter_FrequencyGrain_Runs_Every_6_Hours',
  [cron.frequency.EVERY_12_HOURS]: 'CronFormatter_FrequencyGrain_Runs_Every_12_Hours',
};

const TEMPLATE_KEYS = {
  [cron.frequency.MONTHLY]: 'CronFormatter_Frequency_MonthlyTemplate',
  [cron.frequency.WEEKLY]: 'CronFormatter_Frequency_WeeklyTemplate',
  [cron.frequency.DAILY]: 'CronFormatter_Frequency_DailyTemplate',
  [cron.frequency.EVERY_6_HOURS]: 'CronFormatter_Frequency_Template_Every_N_Hours',
  [cron.frequency.EVERY_12_HOURS]: 'CronFormatter_Frequency_Template_Every_N_Hours',
};

const BACKWARD_COMPATIBLE_ENABLED_FREQUENCIES = [
  cron.frequency.NONE,
  cron.frequency.MONTHLY,
  cron.frequency.WEEKLY,
  cron.frequency.DAILY,
];

function stringToDate(startedAt) {
  let startedAtDateTime = startedAt;

  if (_.isString(startedAt)) {
    startedAtDateTime = new Date(startedAt);
  }

  if (!_.isDate(startedAtDateTime) || _.isNaN(startedAtDateTime.valueOf())) {
    throw Error('Expected startedAt to be a valid date.');
  }

  return startedAtDateTime;
}

/**
 * Formats cron strings and enumerates date ranges for building cron strings.
 */
export class CronFormatter {
  /**
   * Construct a CronFormatter object.
   * @param {Object} i18n - the I18nModel object which can format dates, units, etc.
   */
  constructor({
    i18n,
  }) {
    this.i18n = i18n;
  }

  /**
   * Localize a cron model.
   *
   * @param {Object} cronModel - a model object which is a result from called cron.parse().
   * @param {Integer} [cronModel.frequency] - the frequency of the schedule.
   * @param {Integer} [cronModel.dayOfMonth] - the day of the month of the schedule.
   * @param {Integer} [cronModel.dayOfWeek] - the day of the week of the schedule.
   * @param {Integer} [cronModel.hour] - the hour of the schedule.
   * @returns {Object} the cron model with supported files replaced by localized values.
   */
  formatCronModel(cronModel) {
    const LOCALIZERS = {
      dayOfMonth: this.formatDayOfMonth.bind(this),
      dayOfWeek: this.formatDayOfWeek.bind(this),
      frequency: this.formatCronFrequency.bind(this),
      hour: this.formatHourOfDay.bind(this),
    };

    const localizedCronModel = {};

    _.each(_.keys(cronModel), (key) => {
      if (_.has(LOCALIZERS, key)) {
        const value = cronModel[key];
        const localizer = LOCALIZERS[key];
        const localizedValue = localizer(value, this.i18n);

        localizedCronModel[key] = localizedValue;
      } else {
        throw new Error(`No localizer for key: ${key}`);
      }
    });

    return localizedCronModel;
  }

  /**
   * Localize a raw cron string.
   *
   * @param {String} cronString - the raw cron string, e.g. "0 16 * * * *"
   * @returns {String} a human-readable, localized representation of the cron string,
   * e.g. "Daily at 4 PM"
   */
  formatCronString(cronString) {
    const cronModel = cron.parse(cronString);
    const { frequency } = cronModel;
    const localizedCronModel = this.formatCronModel(cronModel);

    const templateKey = TEMPLATE_KEYS[frequency];

    if (!templateKey) {
      throw new Error(`No template key for frequency: ${frequency}`);
    }

    const localizedCronString = this.i18n.getString(templateKey, localizedCronModel);

    if (!localizedCronString) {
      throw new Error('localizedCronString was empty, is i18n configured correctly?');
    }

    return localizedCronString;
  }

  /**
   * Localize a date time into full format. E.g. "Monday, January 29, 2018 at 4:37:26 PM GMT-05:00"
   *
   * @param {String|Date} startedAt - the datetime to be formatted.
   * @param {Object} options - globalize date format options.
   * @returns {String} the formated "started at" string.
   */
  formatDateTimeLong(startedAt, options) {
    const startedAtDateTime = stringToDate(startedAt);

    return this.i18n.formatDate(startedAtDateTime, _.defaults({}, options, { datetime: 'long' }));
  }

  /**
   * Localize a date time into short format. E.g. "1/29/18, 4:44 PM"
   *
   * @param {String|Date} startedAt - the datetime to be formatted.
   * @param {Object} options - globalize date format options.
   * @returns {String} the formated "started at" string.
   */
  formatDateTimeShort(startedAt, options) {
    const startedAtDateTime = stringToDate(startedAt);

    return this.i18n.formatDate(startedAtDateTime, _.defaults({}, options, { datetime: 'short' }));
  }

  /**
   * Localize the day of month.
   *
   * @param {String|Integer} dayOfMonth - the day of month (1-31).
   * @returns {String} the human-readable, localized representation of the day of the month.
   */
  formatDayOfMonth(dayOfMonth) {
    return String(dayOfMonth);
  }

  /**
   * Localize the day of week.
   *
   * @param {String|Integer} dayOfWeek - the day of week (0 - 6) (Sunday=0).
   * @returns {String} the human-readable, localized representation of the day of the week.
   */
  formatDayOfWeek(dayOfWeek) {
    return this.i18n.formatDate(new Date(Date.UTC(2017, 0, dayOfWeek + 1)), { skeleton: 'EEEE', timeZone: 'UTC' });
  }

  /**
   * Localizes a duration of a task. E.g. "30 sec."
   *
   * @param {Number} durationInSeconds The duration in seconds the task ran.
   * @returns {String} the localized duration string.
   */
  formatDuration(durationInSeconds) {
    const SECONDS_PER_MINUTE = 60;

    if (!_.isNumber(durationInSeconds) || durationInSeconds < 0) {
      throw Error('Expected durationInSeconds to be a positive Number');
    }

    let localizedDuration;
    const durationInMinutes = durationInSeconds / SECONDS_PER_MINUTE;

    if (durationInMinutes >= 1) {
      // If it is 1 minute or greater
      const flooredMinutes = Math.floor(durationInMinutes);

      localizedDuration = this.i18n.formatUnit(flooredMinutes, 'minute', { form: 'short' });
    } else {
      // It is less than 1 minute
      const flooredSeconds = Math.floor(durationInSeconds);

      localizedDuration = this.i18n.formatUnit(flooredSeconds, 'second', { form: 'short' });
    }

    return localizedDuration;
  }

  /**
   * Localize the hour of day.
   *
   * @param {String|Integer} hour - the hour of day (0 - 23).
   * @returns {String} the human-readable, localized representation of the hour of day.
   */
  formatHourOfDay(hour) {
    return this.i18n.formatDate(new Date(Date.UTC(2017, 0, 1, hour)), { skeleton: 'h', timeZone: 'UTC' });
  }

  /**
   * Localize a cron frequency enum defined in cron lib.
   *
   * @param {String|Integer} frequency - the frequency enum value (defined in the cron lib).
   * @returns {String} the human-readable, localized representation of the frequency enum.
   */
  formatCronFrequency(frequency) {
    const frequencyKey = FREQUENCY_KEYS_LOWER[frequency];

    const localizedFrequencyString = this.i18n.getString(frequencyKey);

    return localizedFrequencyString;
  }

  formatCronFrequencyUpperCase(frequency) {
    const frequencyKey = FREQUENCY_KEYS_UPPER[frequency];

    return this.i18n.getString(frequencyKey);
  }

  /**
   * Enumerates localized frequency grains.
   * @param {Number[]} [enabledFrequencies=BACKWARD_COMPATIBLE_ENABLED_FREQUENCIES]
   * array of frequencies to include
   * @returns {Object} map of cron frequency enum to localized frequencies.
   */
  getCronFrequencies(enabledFrequencies = BACKWARD_COMPATIBLE_ENABLED_FREQUENCIES) {
    return _.chain(FREQUENCY_KEYS_UPPER)
      .pick(enabledFrequencies)
      .mapObject(key => this.i18n.getString(key))
      .value();
  }

  /**
   * Returns the enum for all possible cron frequencies.
   * @returns {Object} Enum of keys (e.g. None) to integers (e.g. -1).
   */
  getCronFrequenciesEnum() {
    return _.clone(cron.frequency);
  }

  /**
   * Enumerates localized days of the month.
   * @returns {Object} map of day index (1 - 31) to formatted days.
   */
  getDaysOfMonth() {
    const dayRange = _.range(1, 32);
    const dayRangeStrings = _.map(dayRange, index => this.formatDayOfMonth(index));
    const localizedDaysOfMonth = _.object(dayRange, dayRangeStrings);

    return localizedDaysOfMonth;
  }

  /**
   * Enumerates localized days of the week.
   * @returns {Object} map of index (0 - 6, 0=Sunday) to localized days of the week
   *  (Sunday, Monday ...).
   */
  getDaysOfWeek() {
    const indexArray = _.range(0, 7);
    const daysOfWeekArray = _.map(indexArray, index => this.formatDayOfWeek(index));
    const daysOfWeek = _.object(indexArray, daysOfWeekArray);

    return daysOfWeek;
  }

  /**
   * Enumerates localized hours of day.
   * @returns {Object} map of hour index (0 - 23) to localized (12-hour-format) hours of the day.
   */
  getHoursOfDay() {
    const indexArray = _.range(0, 24);
    const hoursOfDayArray = _.map(indexArray, index => this.formatHourOfDay(index));
    const hoursOfDay = _.object(indexArray, hoursOfDayArray);

    return hoursOfDay;
  }

  /**
   * Parse a cron string into a cron model.
   *
   * @param {String} cronString - a raw cron string.
   * @returns {Object} a cron model which may contain fields min, hour, dayOfWeek, dayOfMonth,
   * month, and year.
   */
  parse(cronString) {
    return cron.parse(cronString);
  }

  /**
   * Converts a cron model into a cron string.
   *
   * @param {Object} cronModel - a model which is the result of cron.parse().
   * @returns {String} a raw cron string.
   */
  toCronString(cronModel) {
    return cron.toCronString(cronModel);
  }
}
